/**
 * Client-side Sentry loader.
 *
 * @sentry/astro looks for this exact file at the project root and injects it
 * into every page automatically. Once it exists, any dsn/options passed to the
 * sentry() integration in astro.config.mjs are ignored for the client side
 * (that integration is only configured for org/project, for source map upload,
 * so there is no conflict here).
 *
 * ── Why this file no longer initializes Sentry itself (September 2026) ──
 * The SDK was the largest script on every page: 85 KB, 28 KB gzipped, parsed
 * and run during page load on phones arriving from Instagram and Facebook. It
 * is only there to report errors, and a page with no errors never needs it.
 * The init now lives in src/lib/sentry-client.ts and is reached only through
 * the dynamic import below, so Vite splits the SDK into its own chunk.
 *
 * ── When it loads ──
 *   - After the page's load event, once the browser is idle, or
 *   - Immediately, the first time an error or unhandled rejection happens.
 * The second rule is what keeps this from being a trade-off. Anything thrown
 * before the SDK is up is caught by the two listeners below, held, and
 * reported by initSentry once it runs. The only errors that can still be lost
 * are ones thrown in the moment between the first error and the chunk
 * arriving, on a visitor who leaves in that same moment.
 *
 * ── What changes in Sentry ──
 * Errors caught early carry the tag captured_before_init, and show as handled
 * because they are replayed through captureException. Breadcrumbs from before
 * the SDK loaded (clicks, console lines, fetches) are not recorded, because
 * nothing was recording them yet.
 */

/** Longest wait for an idle moment after load before loading anyway. */
const IDLE_TIMEOUT_MS = 3000;

const capturedBeforeInit: unknown[] = [];
let loading = false;

function capture(error: unknown): void {
  capturedBeforeInit.push(error);
  load();
}

function onError(event: ErrorEvent): void {
  capture(event.error ?? event.message);
}

function onRejection(event: PromiseRejectionEvent): void {
  capture(event.reason);
}

function load(): void {
  if (loading) return;
  loading = true;
  void import("./src/lib/sentry-client").then(({ initSentry }) => {
    // Stop buffering before the SDK's own handlers take over, so nothing is
    // reported twice. Anything still in the array goes out with the init.
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
    initSentry(capturedBeforeInit);
  });
}

function loadWhenIdle(): void {
  if ("requestIdleCallback" in window) {
    requestIdleCallback(load, { timeout: IDLE_TIMEOUT_MS });
  } else {
    setTimeout(load, 0);
  }
}

window.addEventListener("error", onError);
window.addEventListener("unhandledrejection", onRejection);

if (document.readyState === "complete") {
  loadWhenIdle();
} else {
  window.addEventListener("load", loadWhenIdle, { once: true });
}
